import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useBranches } from "@/hooks/useBranches";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { resolveBranchId } from "@/hooks/branchResolution";

interface BranchContextValue {
  currentBranchId: number | null;
  setCurrentBranchId: (id: number) => void;
  branches: { id: number; name: string; code: string | null; is_active: boolean | null }[];
  assignedBranchIds: number[];
  isLoading: boolean;
  isAdmin: boolean;
  userBranchId: number | null;
  assignedLoading: boolean;
  authLoading: boolean;
}

const BranchContext = createContext<BranchContextValue | undefined>(undefined);

const STORAGE_KEY = "currentBranchId";

export const BranchProvider = ({ children }: { children: React.ReactNode }) => {
  const { data: branches = [], isLoading: branchesLoading } = useBranches();
  const { userProfile, isAdmin, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const userBranchId = userProfile?.branch_id ?? null;
  const [currentBranchId, setCurrentBranchIdState] = useState<number | null>(null);
  const { data: assignedBranchIds = [], isLoading: assignedLoading } = useQuery({
    queryKey: ["profile-branches", userProfile?.id],
    enabled: !!userProfile?.id,
    queryFn: async () => {
      // @ts-ignore - profile_branches table may not be in generated types yet
      const { data, error } = await (supabase as any)
        .from("profile_branches")
        .select("branch_id")
        .eq("profile_id", userProfile!.id);
      if (error) throw error;
      return (data || [])
        .map((row: any) => row.branch_id)
        .filter((id: unknown): id is number => typeof id === "number");
    },
  });

  useEffect(() => {
    if (authLoading) return;
    if (!branches.length) return;
    if (!isAdmin && assignedLoading) return;

    const stored = localStorage.getItem(STORAGE_KEY);
    const storedId = stored ? Number(stored) : null;
    const effectiveAssigned =
      assignedBranchIds.length > 0
        ? assignedBranchIds
        : userBranchId != null
          ? [userBranchId]
          : [];
    const allowedBranchIds = isAdmin ? branches.map((b) => b.id) : effectiveAssigned;
    const currentBranch =
      currentBranchId != null && allowedBranchIds.includes(currentBranchId)
        ? branches.find((b) => b.id === currentBranchId)
        : null;
    const currentValid = currentBranch != null && currentBranch.is_active === true;
    const storedBranch =
      storedId != null && allowedBranchIds.includes(storedId)
        ? branches.find((b) => b.id === storedId)
        : null;
    const storedValid = storedBranch != null;
    const storedActive = storedBranch?.is_active === true;

    if (currentValid) return;

    let nextId: number | null = null;
    if (storedValid && !storedActive) {
      toast.error("Selected branch is inactive. Switched to an active branch.");
    }

    nextId = resolveBranchId({
      storedId,
      storedValid,
      storedActive,
      allowedBranchIds,
      effectiveAssigned,
      userBranchId,
      branches,
    });

    if (nextId != null) {
      setCurrentBranchIdState(nextId);
      localStorage.setItem(STORAGE_KEY, String(nextId));
    }
  }, [branches, isAdmin, userBranchId, assignedBranchIds, assignedLoading, authLoading]);

  const setCurrentBranchId = (id: number) => {
    const effectiveAssigned =
      assignedBranchIds.length > 0
        ? assignedBranchIds
        : userBranchId != null
          ? [userBranchId]
          : [];
    if (!isAdmin && !effectiveAssigned.includes(id)) {
      toast.error("You do not have access to this branch.");
      return;
    }
    const targetBranch = branches.find((b) => b.id === id);
    if (!targetBranch || targetBranch.is_active !== true) {
      toast.error("This branch is inactive.");
      return;
    }
    setCurrentBranchIdState(id);
    localStorage.setItem(STORAGE_KEY, String(id));
    // Exclude notifications - invalidating causes refetch that can reset read state
    queryClient.invalidateQueries({
      predicate: (query) => !query.queryKey.includes("notifications"),
    });
  };

  const value = useMemo(
    () => ({
      currentBranchId,
      setCurrentBranchId,
      branches,
      assignedBranchIds,
      isLoading: branchesLoading,
      isAdmin,
      userBranchId,
      assignedLoading,
      authLoading,
    }),
    [currentBranchId, branches, assignedBranchIds, branchesLoading, isAdmin, userBranchId, assignedLoading, authLoading]
  );

  return <BranchContext.Provider value={value}>{children}</BranchContext.Provider>;
};

export const useBranchContext = () => {
  const context = useContext(BranchContext);
  if (!context) {
    throw new Error("useBranchContext must be used within a BranchProvider");
  }
  return context;
};
